import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';

/**
 * Leave, from the browser's side.
 *
 * ── Why nothing here writes a row ──────────────────────────────────────────
 *
 * Every mutation below is an RPC. There is no insert, update or delete grant
 * on leave_requests at any level — the state machine, the ownership checks and
 * the approver check all live in security-definer functions, so a `.insert()`
 * from here would simply be refused. That is the point: the rules cannot be
 * routed around by a stale tab or by a future query written in a hurry.
 *
 * ── Why `reason` is missing from the column list ───────────────────────────
 *
 * Because it is not selectable. A leave reason is medical often enough that it
 * sits outside the table's select grant entirely, and the only way to it is
 * fetchLeaveReason() below, which the database refuses unless you filed the
 * request or you are an approver. A list, a badge or a future calendar cell
 * physically cannot leak one, because the row it reads does not contain it.
 */

// One literal, unwrapped (PostgREST type inference).
// prettier-ignore
const LEAVE_COLUMNS = 'id, employee_id, submitted_by, leave_date, leave_type, duration_type, start_time, end_time, status, edit_count, reviewed_by, reviewed_at, cancel_requested_at, cancel_reviewed_by, cancel_reviewed_at, created_at, updated_at';

export type LeaveStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCEL_REQUESTED' | 'CANCELLED';

export type LeaveType = 'PTO' | 'Unpaid Leave';
export type DurationType = 'Full Day' | 'Half Day';

export interface LeaveRequest {
  id: string;
  employee_id: string;
  submitted_by: string;
  leave_date: string;
  leave_type: string;
  duration_type: string;
  start_time: string | null;
  end_time: string | null;
  status: string;
  edit_count: number;
  reviewed_by: string | null;
  reviewed_at: string | null;
  cancel_requested_at: string | null;
  cancel_reviewed_by: string | null;
  cancel_reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface FileLeaveInput {
  leaveDate: string;
  leaveType: LeaveType;
  durationType: DurationType;
  startTime?: string | undefined;
  endTime?: string | undefined;
  reason?: string | undefined;
}

function fail(message: string): never {
  throw new Error(message);
}

export const leaveKeys = {
  all: ['leave'] as const,
  mine: (userId: string) => [...leaveKeys.all, 'mine', userId] as const,
  reason: (requestId: string) => [...leaveKeys.all, 'reason', requestId] as const,
};

export async function fetchMyLeave(
  client: SupabaseClient,
  userId: string,
): Promise<LeaveRequest[]> {
  const { data, error } = await client
    .from('leave_requests')
    .select(LEAVE_COLUMNS)
    .eq('employee_id', userId)
    .order('leave_date', { ascending: false });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

/**
 * The reason, one request at a time, and only when you are allowed it.
 *
 * Deliberately not batched into the list query. Fetching every reason to
 * render a list that shows none of them would defeat the whole arrangement:
 * the value would sit in the browser's memory and in the network tab for
 * anybody looking over a shoulder.
 */
export async function fetchLeaveReason(
  client: SupabaseClient,
  requestId: string,
): Promise<{ reason: string; adminNote: string }> {
  const { data, error } = await client.rpc('leave_request_private', { p_request: requestId });
  if (error) {
    fail(error.message);
  }
  const row = Array.isArray(data) ? data[0] : undefined;
  return {
    reason: typeof row?.reason === 'string' ? row.reason : '',
    adminNote: typeof row?.admin_note === 'string' ? row.admin_note : '',
  };
}

export async function fileLeave(client: SupabaseClient, input: FileLeaveInput): Promise<string> {
  // The hours are SPREAD IN rather than passed as undefined: with
  // exactOptionalPropertyTypes on, an optional argument must be absent, not
  // present-and-undefined. A full day sends neither, and the function's own
  // defaults leave both null — which is what the hours-match-duration
  // constraint requires.
  const halfDay =
    input.durationType === 'Half Day' &&
    input.startTime !== undefined &&
    input.endTime !== undefined
      ? { p_start_time: input.startTime, p_end_time: input.endTime }
      : {};
  const { data, error } = await client.rpc('file_leave_request', {
    p_leave_date: input.leaveDate,
    p_leave_type: input.leaveType,
    p_duration_type: input.durationType,
    ...halfDay,
    p_reason: input.reason ?? '',
  });
  if (error) {
    fail(error.message);
  }
  return typeof data === 'string' ? data : '';
}

/**
 * Withdraw, or ask to.
 *
 * One function for both, because from the person's side it is one action:
 * "I don't need this day any more." The database decides which it is — a
 * PENDING request is simply withdrawn, an APPROVED one becomes a request an
 * approver has to answer. The button's label changes; the call does not.
 */
export async function withdrawLeave(client: SupabaseClient, requestId: string): Promise<void> {
  const { error } = await client.rpc('request_leave_cancellation', { p_request: requestId });
  if (error) {
    fail(error.message);
  }
}

/** The sentence a person should see, for the errors this screen can produce. */
export function leaveErrorMessage(raw: string): string {
  if (raw.includes('leave_reason_not_yours')) {
    return 'That reason is only visible to the person who filed it and to an approver.';
  }
  if (raw.includes('leave_already_decided')) {
    return 'This has already been decided, so it can no longer be edited.';
  }
  if (raw.includes('leave_cannot_be_cancelled')) {
    return 'Only a pending or approved day can be withdrawn.';
  }
  if (raw.includes('leave_not_yours')) {
    return 'This is not your request.';
  }
  if (raw.includes('leave_not_an_approver')) {
    return 'Only Kyu or Alex can decide leave.';
  }
  if (raw.includes('leave_requests_one_live_per_day_idx')) {
    return 'You already have a live request for that date.';
  }
  if (raw.includes('leave_requests_hours_match_duration')) {
    return 'A half day needs a start and an end time, and the end must be later.';
  }
  if (raw.includes('leave_not_permitted')) {
    return 'Leave is for the TAP team.';
  }
  if (raw.includes('leave_delete_not_approver')) {
    return 'Only an approver can remove a request.';
  }
  if (raw.includes('leave_delete_needs_note')) {
    return 'Say why it is being removed. The note is the only record left afterwards.';
  }
  if (raw.includes('leave_delete_only_pending')) {
    return 'Only a request nobody has decided yet can be removed. Withdraw an approved day instead.';
  }
  if (raw.includes('leave_request_not_found')) {
    return 'That request is no longer there. Somebody may have removed it.';
  }
  return raw;
}

/**
 * The approver queue, grouped in the database.
 *
 * groupConsecutive() in the Apps Script groups by employee, type, duration,
 * hours AND reason. The reason is not readable from here, so the grouping is
 * done where it is readable and only the group number comes back. Same
 * grouping, and the reason still never leaves the database.
 */
export interface ApprovalQueueRow {
  id: string;
  employee_id: string;
  employee_name: string;
  leave_date: string;
  leave_type: string;
  duration_type: string;
  start_time: string | null;
  end_time: string | null;
  submitted_by: string;
  edit_count: number;
  created_at: string;
  group_no: number;
}

export interface CancellationQueueRow {
  id: string;
  employee_id: string;
  employee_name: string;
  leave_date: string;
  leave_type: string;
  duration_type: string;
  start_time: string | null;
  end_time: string | null;
  cancel_requested_at: string;
}

export interface DecisionConflict {
  candidate_id: string;
  candidate_name: string;
  leave_date: string;
  other_id: string;
  other_name: string;
}

/** What decide_leave_requests answers with. Mirrors the Apps Script result. */
export type DecisionResult =
  | { ok: true; decided: number }
  | { ok: false; requiresOverride: true; conflicts: DecisionConflict[]; message: string };

export async function fetchApprovalQueue(client: SupabaseClient): Promise<ApprovalQueueRow[]> {
  const { data, error } = await client.rpc('leave_approval_queue');
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export async function fetchCancellationQueue(
  client: SupabaseClient,
): Promise<CancellationQueueRow[]> {
  const { data, error } = await client.rpc('leave_cancellation_queue');
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

/**
 * Approve or reject.
 *
 * Two things this deliberately does NOT smooth over, because the Apps Script
 * does neither:
 *
 *   - A row somebody else already decided throws, naming the person and the
 *     date, and nothing in the batch is decided.
 *   - An overlap on approve comes back as requiresOverride with the conflicts
 *     to show, having decided nothing. The approver confirms and it is sent
 *     again with override true.
 */
export async function decideLeave(
  client: SupabaseClient,
  ids: string[],
  approve: boolean,
  note: string,
  override = false,
): Promise<DecisionResult> {
  const { data, error } = await client.rpc('decide_leave_requests', {
    p_ids: ids,
    p_approve: approve,
    p_note: note,
    p_override: override,
  });
  if (error) {
    fail(error.message);
  }
  const result = data as Record<string, unknown> | null;
  if (result?.['requires_override'] === true) {
    return {
      ok: false,
      requiresOverride: true,
      conflicts: (result['conflicts'] ?? []) as DecisionConflict[],
      message: typeof result['message'] === 'string' ? result['message'] : 'Overlapping leave.',
    };
  }
  return { ok: true, decided: typeof result?.['decided'] === 'number' ? result['decided'] : 0 };
}

export async function decideCancellation(
  client: SupabaseClient,
  requestId: string,
  approve: boolean,
  note: string,
): Promise<void> {
  const { error } = await client.rpc('decide_leave_cancellation', {
    p_request: requestId,
    p_approve: approve,
    p_note: note,
  });
  if (error) {
    fail(error.message);
  }
}

export interface CalendarRow {
  id: string;
  employee_id: string;
  employee_name: string;
  leave_date: string;
  leave_type: string;
  duration_type: string;
  start_time: string | null;
  end_time: string | null;
  status: string;
  colour_no: number;
}

export async function fetchLeaveCalendar(client: SupabaseClient): Promise<CalendarRow[]> {
  const { data, error } = await client.rpc('leave_calendar');
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export function useLeaveCalendar() {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...leaveKeys.all, 'calendar'],
    queryFn: () => fetchLeaveCalendar(client),
  });
}

export function useApprovalQueue(enabled: boolean) {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...leaveKeys.all, 'queue'],
    queryFn: () => fetchApprovalQueue(client),
    enabled,
  });
}

export function useCancellationQueue(enabled: boolean) {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...leaveKeys.all, 'cancellations'],
    queryFn: () => fetchCancellationQueue(client),
    enabled,
  });
}

export function useDecideLeave() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: string[]; approve: boolean; note: string; override?: boolean }) =>
      decideLeave(client, input.ids, input.approve, input.note, input.override ?? false),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: leaveKeys.all });
    },
  });
}

export function useDecideCancellation() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { requestId: string; approve: boolean; note: string }) =>
      decideCancellation(client, input.requestId, input.approve, input.note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: leaveKeys.all });
    },
  });
}

/** Is the signed-in person allowed to decide leave? */
export function useIsLeaveApprover() {
  const { client, access } = useAuth();
  const userId = access.status === 'approved' ? access.profile.id : '';
  return useQuery({
    queryKey: [...leaveKeys.all, 'approver', userId],
    queryFn: async () => {
      const { data, error } = await client
        .from('profiles')
        .select('is_leave_approver')
        .eq('id', userId)
        .maybeSingle();
      if (error) {
        fail(error.message);
      }
      return data?.is_leave_approver === true;
    },
    enabled: userId !== '',
  });
}

export function useMyLeave() {
  const { client, access } = useAuth();
  // Only an approved profile has an id, and only an approved profile can reach
  // this page at all. Anything else leaves the query disabled rather than
  // firing a request that would return nothing.
  const userId = access.status === 'approved' ? access.profile.id : '';
  return useQuery({
    queryKey: leaveKeys.mine(userId),
    queryFn: () => fetchMyLeave(client, userId),
    enabled: userId !== '',
  });
}

export function useLeaveReason(requestId: string | null) {
  const { client } = useAuth();
  return useQuery({
    queryKey: leaveKeys.reason(requestId ?? ''),
    queryFn: () => fetchLeaveReason(client, requestId ?? ''),
    enabled: requestId !== null,
  });
}

export function useFileLeave() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: FileLeaveInput) => fileLeave(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: leaveKeys.all });
    },
  });
}

export function useWithdrawLeave() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => withdrawLeave(client, requestId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: leaveKeys.all });
    },
  });
}

// ── All Requests, drop 6 ────────────────────────────────────────────────────
//
// The one read path that returns reasons for a SET of requests. It is an RPC
// rather than a select for the same reason every write here is: `reason` and
// `admin_note` sit outside the table's column grant, and widening that grant
// so one screen could use it would widen it for every screen.
//
// The filters are arguments rather than a `.filter()` in the browser. Sending
// every row and hiding some of it is not a filter, and these rows carry
// reasons.

export interface AllRequestRow {
  id: string;
  employee_id: string;
  employee_name: string;
  leave_date: string;
  leave_type: string;
  duration_type: string;
  start_time: string | null;
  end_time: string | null;
  status: string;
  reason: string;
  admin_note: string;
  edit_count: number;
  reviewed_by: string | null;
  reviewer_name: string | null;
  reviewed_at: string | null;
  created_at: string;
}

export interface AllRequestFilter {
  /** Inclusive, or '' for no bound. */
  from: string;
  to: string;
  /** A profile id, or '' for everyone. */
  employeeId: string;
  /** A LeaveStatus, or '' for every status. */
  status: string;
}

export const EMPTY_ALL_FILTER: AllRequestFilter = {
  from: '',
  to: '',
  employeeId: '',
  status: '',
};

/**
 * '' means "no bound", and an unset argument is how that is said here.
 *
 * NOT null. The generated RPC types make every defaulted argument optional
 * rather than nullable, and `exactOptionalPropertyTypes` then refuses a
 * present-and-undefined key. So an unused filter is ABSENT from the payload
 * and the function's own `default null` supplies the rest. Same shape as the
 * half-day hours in fileLeave() above, and for the same reason.
 */
function argOf(key: string, value: string): Record<string, string> {
  return value.trim() === '' ? {} : { [key]: value };
}

export async function fetchAllRequests(
  client: SupabaseClient,
  filter: AllRequestFilter,
): Promise<AllRequestRow[]> {
  const { data, error } = await client.rpc('leave_all_requests', {
    ...argOf('p_from', filter.from),
    ...argOf('p_to', filter.to),
    ...argOf('p_employee', filter.employeeId),
    ...argOf('p_status', filter.status),
  });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export interface AdminEditInput {
  requestId: string;
  leaveDate: string;
  leaveType: LeaveType;
  durationType: DurationType;
  startTime?: string | undefined;
  endTime?: string | undefined;
  reason: string;
  note: string;
}

/**
 * An approver editing somebody else's request.
 *
 * Deliberately NOT editLeave(): `edit_leave_request` is the member's own path
 * and refuses anybody but the person who filed it, PENDING only, with no note.
 * The Apps Script's admin edit works on an approved request and carries a
 * note — the Sheet's log has it doing exactly that on 11 August. Two different
 * rules, so two different functions.
 */
export async function adminEditLeave(client: SupabaseClient, input: AdminEditInput): Promise<void> {
  // Absent, not present-and-undefined: exactOptionalPropertyTypes is on, and
  // a full day must send neither hour or the duration constraint refuses it.
  const halfDay =
    input.durationType === 'Half Day' &&
    input.startTime !== undefined &&
    input.endTime !== undefined
      ? { p_start_time: input.startTime, p_end_time: input.endTime }
      : {};
  const { error } = await client.rpc('admin_edit_leave_request', {
    p_request: input.requestId,
    p_leave_date: input.leaveDate,
    p_leave_type: input.leaveType,
    p_duration_type: input.durationType,
    ...halfDay,
    p_reason: input.reason,
    p_note: input.note,
  });
  if (error) {
    fail(error.message);
  }
}

/** Approver-only, PENDING-only, and the note is not optional. */
export async function deleteLeaveRequest(
  client: SupabaseClient,
  requestId: string,
  note: string,
): Promise<void> {
  const { error } = await client.rpc('delete_leave_request', {
    p_request: requestId,
    p_note: note,
  });
  if (error) {
    fail(error.message);
  }
}

export function useAllRequests(filter: AllRequestFilter, enabled: boolean) {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...leaveKeys.all, 'allRequests', filter],
    queryFn: () => fetchAllRequests(client, filter),
    enabled,
  });
}

export function useAdminEditLeave() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AdminEditInput) => adminEditLeave(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: leaveKeys.all });
    },
  });
}

export function useDeleteLeaveRequest() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { requestId: string; note: string }) =>
      deleteLeaveRequest(client, input.requestId, input.note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: leaveKeys.all });
    },
  });
}
